import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Checkbox, Input, useTheme, spacing } from '@fti/design-system';
import { ColumnTypeBadge } from '../ColumnTypeSystem/ColumnTypeBadge';
import { TypeSelector } from '../ColumnTypeSystem/TypeSelector';
import type { ColumnType } from '../../types/columnType';

interface DataGridHeaderProps {
  columns: string[];
  columnTypes: ColumnType[];
  isColumnDeleted: (index: number) => boolean;
  getColumnName: (index: number, original: string) => string;
  isColumnRenamed: (index: number) => boolean;
  onRenameColumn?: (columnIndex: number, newName: string, previousName: string) => void;
  onTypeChange?: (columnIndex: number, newType: ColumnType) => void;
  allColumns: string[]; // For duplicate validation
  selectedColumns: Set<number>;
  onSelectColumn: (columnIndex: number, selected: boolean) => void;
}

export const DataGridHeader: React.FC<DataGridHeaderProps> = ({
  columns,
  columnTypes,
  isColumnDeleted,
  getColumnName,
  isColumnRenamed,
  onRenameColumn,
  onTypeChange,
  allColumns,
  selectedColumns,
  onSelectColumn,
}) => {
  const { theme } = useTheme();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [openTypeSelector, setOpenTypeSelector] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingIndex !== null && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingIndex]);

  const handleDoubleClick = useCallback(
    (colIdx: number) => {
      if (isColumnDeleted(colIdx) || !onRenameColumn) return;
      const currentName = getColumnName(colIdx, columns[colIdx]);
      setEditingIndex(colIdx);
      setEditValue(currentName);
      setValidationError(null);
    },
    [isColumnDeleted, onRenameColumn, getColumnName, columns]
  );

  const validateName = useCallback(
    (name: string, currentIndex: number): string | null => {
      if (!name.trim()) return 'Column name cannot be empty';
      const otherColumns = allColumns
        .map((col, idx) => (idx === currentIndex ? null : getColumnName(idx, col)))
        .filter(Boolean) as string[];
      if (otherColumns.includes(name.trim())) return 'Column name already exists';
      return null;
    },
    [allColumns, getColumnName]
  );

  const commitRename = useCallback(() => {
    if (editingIndex === null || !onRenameColumn) return;
    const error = validateName(editValue, editingIndex);
    if (error) {
      setValidationError(error);
      return;
    }
    const originalName = columns[editingIndex];
    const currentName = getColumnName(editingIndex, originalName);
    if (editValue.trim() !== currentName) {
      onRenameColumn(editingIndex, editValue.trim(), currentName);
    }
    setEditingIndex(null);
    setValidationError(null);
  }, [editingIndex, editValue, onRenameColumn, columns, getColumnName, validateName]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitRename();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setEditingIndex(null);
        setValidationError(null);
      }
    },
    [commitRename]
  );

  const handleTypeChange = useCallback(
    (colIdx: number, newType: ColumnType) => {
      onTypeChange?.(colIdx, newType);
      setOpenTypeSelector(null);
    },
    [onTypeChange]
  );

  return (
    <thead>
      <tr style={{ backgroundColor: theme.colors.neutral[50] }}>
        <th
          style={{
            padding: spacing.sm,
            width: '40px',
            textAlign: 'center',
            borderBottom: `2px solid ${theme.colors.border.default}`,
          }}
        >
          {/* Row checkbox column header — intentionally empty */}
        </th>
        {columns.map((col, idx) => {
          if (isColumnDeleted(idx)) return null;

          const displayName = getColumnName(idx, col);
          const renamed = isColumnRenamed(idx);
          const isEditingThis = editingIndex === idx;
          const isSelected = selectedColumns.has(idx);

          return (
            <th
              key={idx}
              style={{
                padding: spacing.md,
                textAlign: 'left',
                fontWeight: 600,
                color: theme.colors.text.primary,
                borderBottom: `2px solid ${theme.colors.border.default}`,
                whiteSpace: 'nowrap',
                backgroundColor: isSelected
                  ? `${theme.colors.semantic.error.light}15`
                  : renamed
                    ? `${theme.colors.semantic.warning.light}15`
                    : undefined,
              }}
              onDoubleClick={() => handleDoubleClick(idx)}
            >
              {isEditingThis ? (
                <div>
                  <Input
                    ref={inputRef}
                    value={editValue}
                    onChange={(e) => {
                      setEditValue(e.target.value);
                      setValidationError(null);
                    }}
                    onKeyDown={handleKeyDown}
                    onBlur={commitRename}
                    size="sm"
                    style={{ minWidth: '80px' }}
                  />
                  {validationError && (
                    <span style={{ fontSize: '0.75rem', color: theme.colors.semantic.error.default }}>
                      {validationError}
                    </span>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => onSelectColumn(idx, (e.target as HTMLInputElement).checked)}
                    aria-label={`Select column ${displayName}`}
                  />
                  <span>{displayName}</span>
                  {columnTypes[idx] && (
                    <div style={{ position: 'relative' }}>
                      <ColumnTypeBadge
                        type={columnTypes[idx]}
                        onClick={() => setOpenTypeSelector(openTypeSelector === idx ? null : idx)}
                      />
                      {openTypeSelector === idx && onTypeChange && (
                        <TypeSelector
                          currentType={columnTypes[idx]}
                          onTypeChange={(newType) => handleTypeChange(idx, newType)}
                          onClose={() => setOpenTypeSelector(null)}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );
};
